import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from '../../../node_modules/.pnpm/classnames@2.5.1/node_modules/classnames/index.js';
import { forwardRef, useRef, useCallback, useImperativeHandle } from 'react';
import { ReactComponent as ForwardRef } from '../../assets/icons/CloseIcon16.svg.js';
import style from './TagInput.module.css.js';

const separators = ["Enter", "Tab", ",", " "];
const separatorRegex = /[\s\t,\n\r]/;
const TagInput = forwardRef(({ deleteOnBackspace = true, allowDuplicates = false, errors, onChange, onAdd, onRemove, onBlur, onFocus, className, values, maxRows, tokenizeOnBlur, ...attrs }, ref) => {
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const tagRefs = useRef([]);
    const clearInput = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.style.setProperty("--dynamic-width", "0ch");
        }
    }, []);
    useImperativeHandle(ref, () => ({
        clearInput,
    }), [clearInput]);
    const scrollToBottom = () => {
        if (wrapperRef.current && maxRows) {
            requestAnimationFrame(() => {
                if (wrapperRef.current) {
                    wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
                }
            });
        }
    };
    const ensureInputVisible = useCallback(() => {
        if (wrapperRef.current && inputRef.current && maxRows) {
            requestAnimationFrame(() => {
                if (!inputRef.current) {
                    return;
                }
                inputRef.current?.scrollIntoView({ block: "nearest" });
            });
        }
    }, [maxRows]);
    const focusTag = useCallback((index) => {
        if (index >= 0 && index < values.length && tagRefs.current[index]) {
            tagRefs.current[index]?.focus();
        }
    }, [values.length]);
    const focusInput = useCallback(() => {
        inputRef.current?.focus();
    }, []);
    const removeTag = useCallback((index) => {
        const newValues = values.filter((_, i) => i !== index);
        onChange?.(newValues);
        onRemove?.(values[index]);
        const removedTagWasFocused = document.activeElement === tagRefs.current[index];
        if (removedTagWasFocused) {
            requestAnimationFrame(() => {
                if (newValues.length === 0) {
                    focusInput();
                }
                else if (index === newValues.length) {
                    focusTag(index - 1);
                }
                else {
                    focusTag(index);
                }
            });
        }
    }, [values, onChange, onRemove, focusInput, focusTag]);
    return (jsxs("div", { ref: wrapperRef, className: classNames(style.wrapper, className, attrs.disabled && style.disabled, maxRows && style.limitedRows), style: { "--max-rows": maxRows }, children: [values?.map((value, index) => (jsxs("div", { ref: (el) => (tagRefs.current[index] = el), className: classNames(style.tag, errors?.includes(value) && style.error), tabIndex: -1, onKeyDown: (e) => {
                    if (attrs.disabled)
                        return;
                    switch (e.key) {
                        case "Delete":
                        case "Backspace":
                            e.preventDefault();
                            removeTag(index);
                            break;
                        case "ArrowLeft":
                            e.preventDefault();
                            if (index > 0) {
                                focusTag(index - 1);
                            }
                            break;
                        case "ArrowRight":
                            e.preventDefault();
                            if (index < values.length - 1) {
                                focusTag(index + 1);
                            }
                            else {
                                focusInput();
                            }
                            break;
                    }
                }, children: [jsx("span", { children: value }), jsx("button", { className: style.tagButton, disabled: attrs.disabled, type: "button", onClick: () => removeTag(index), tabIndex: -1, children: jsx(ForwardRef, {}) })] }, `${index}-${value}`))), jsx("input", { type: "text", ...attrs, placeholder: attrs.placeholder || undefined, className: classNames(style.textField), ref: inputRef, onFocus: () => {
                    onFocus?.();
                }, onInput: (e) => {
                    ensureInputVisible();
                    e.currentTarget.style.setProperty("--dynamic-width", `${e.currentTarget.value.length}ch`);
                    attrs.onInput?.(e);
                }, onBlur: (e) => {
                    const value = e.currentTarget.value.trim();
                    if (tokenizeOnBlur) {
                        if (value) {
                            onChange?.([...values, value]);
                            onAdd?.(value);
                            clearInput();
                        }
                    }
                    onBlur?.([...values, value]);
                }, onPaste: (e) => {
                    e.preventDefault();
                    const value = e.clipboardData.getData("text");
                    if (!value) {
                        return;
                    }
                    const splitValues = value.split(separatorRegex).filter((v) => v.trim() !== "");
                    let dedupedValues = splitValues;
                    if (!allowDuplicates) {
                        dedupedValues = [...new Set(splitValues)].filter((v) => !values.includes(v));
                    }
                    onChange?.([...values, ...dedupedValues]);
                    dedupedValues.forEach((v) => onAdd?.(v));
                    if (dedupedValues.length > 0) {
                        scrollToBottom();
                    }
                }, onKeyDown: (e) => {
                    attrs.onKeyDown?.(e);
                    const isSeparatorAndNotEmpty = separators?.includes(e.key) && e.currentTarget.value.trim() !== "";
                    if (isSeparatorAndNotEmpty) {
                        e.preventDefault();
                    }
                    const value = e.currentTarget.value.trim();
                    if (!value && e.key === "ArrowLeft" && values.length > 0) {
                        e.preventDefault();
                        focusTag(values.length - 1);
                        return;
                    }
                    if (!value) {
                        if (e.key === "Backspace" &&
                            deleteOnBackspace &&
                            (values?.length ?? 0) > 0 &&
                            (e.metaKey || e.ctrlKey)) {
                            onChange?.([]);
                            values?.forEach((v) => onRemove?.(v));
                        }
                        else if (e.key === "Backspace" && deleteOnBackspace && (values?.length ?? 0) > 0) {
                            e.preventDefault();
                            focusTag(values.length - 1);
                        }
                        return;
                    }
                    if (isSeparatorAndNotEmpty) {
                        if (!allowDuplicates && values?.includes(value)) {
                            clearInput();
                            return;
                        }
                        onChange?.([...values, value]);
                        onAdd?.(value);
                        clearInput();
                        scrollToBottom();
                    }
                } })] }));
});
TagInput.displayName = "TagInput";

export { TagInput };
//# sourceMappingURL=TagInput.js.map
